import { urlUtils, isShiftXUrl } from '@ardoq/common-helpers';
import {
  APIComponentAttributes,
  APIFieldAttributes,
  APIFieldType,
  APIOrganizationUser,
  APIReferenceAttributes,
  ArdoqId,
  isContentTypeSupportedByImagePreview,
} from '@ardoq/api-types';
import { FormattedDate } from 'atomicComponents/FormattedDate';
import { formatDateShort } from '@ardoq/date-time';
import { Markdown } from 'atomicComponents/Markdown';
import styled from 'styled-components';
import { colors } from '@ardoq/design-tokens';
import { NoValue } from '@ardoq/renderers';
import { FieldValues } from './types';
import { TAGS_FIELD_NAME } from '@ardoq/data-model';
import { DateRangeFieldValues, dateRangeOperations } from '@ardoq/date-range';
import { logError } from '@ardoq/logging';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { WithPopover } from '@ardoq/popovers';
import { formatNumber, type Locale } from '@ardoq/locale';
import { Checkbox } from '@ardoq/forms';
import { FlexBox } from '@ardoq/layout';
import { Link } from '@ardoq/link';
import { showImagePreviewModal } from '@ardoq/image-preview';
import {
  getImagePreviewErrorMessages,
  getImagePreviewLabels,
  getImagePreviewModalChrome,
} from 'locales/getImagePreviewLabels';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { type I18nLocale } from '@ardoq/i18n';
import { type AttachmentWithContext } from 'streams/documentArchive/types';
import { UserDecorator } from '@ardoq/decorators';
import { getUserImage } from 'streams/orgUsers/orgUsersUtils';

type EntityAttributes = APIComponentAttributes | APIReferenceAttributes;

const getFileComponent = (
  id: string,
  attachmentsById: Record<ArdoqId, AttachmentWithContext>
) => {
  const file = attachmentsById[id];

  if (!file) return `${id} (File not found)`;

  const isSupportedByImagePreview = isContentTypeSupportedByImagePreview(file);

  if (!isSupportedByImagePreview) {
    return (
      <Link
        hideIcon
        underlineOnHover
        href={file.uri}
        target="_blank"
        rel="noopener noreferrer"
      >
        {file.filename}
      </Link>
    );
  }

  return (
    <Link
      hideIcon
      underlineOnHover
      onClick={() => {
        // i18nLocale$.state OK here: snapshot-at-open-time; @ardoq/modal renders modal() body once and does not re-render on locale change
        const i18nLocale = i18nLocale$.state;
        const { downloadLinkLabel, closeButtonText } =
          getImagePreviewModalChrome(i18nLocale);
        showImagePreviewModal({
          title: file.filename,
          url: file.uri,
          link: { href: file.uri, label: downloadLinkLabel },
          closeButtonText,
          labels: getImagePreviewLabels(i18nLocale),
          errorMessages: getImagePreviewErrorMessages(i18nLocale),
        });
      }}
    >
      {file.filename}
    </Link>
  );
};

const getUser = (
  attributes: EntityAttributes | null,
  field: Partial<APIFieldAttributes>,
  val: string,
  usersById: Record<ArdoqId, APIOrganizationUser>
) => {
  const { name, email } = getNameAndEmail(attributes, field, val, usersById);
  if (!name && !email) return null;

  const image = getUserImage(usersById, val);

  return <UserDecorator name={name} email={email} image={image} />;
};

const getNameAndEmail = (
  attributes: EntityAttributes | null,
  field: Partial<APIFieldAttributes>,
  val: string,
  usersById: Record<ArdoqId, APIOrganizationUser>
) => {
  const user = usersById[val];

  if (user) {
    return { name: user.name, email: user.email };
  }

  if (field.name === 'created-by') {
    const { createdByName: name, createdByEmail: email } = getEntityAttributes(
      attributes,
      ['createdByName', 'createdByEmail']
    );
    return {
      name: name as string | undefined,
      email: email as string | undefined,
    };
  }

  if (field.name === 'last-modified-by') {
    const { lastModifiedByName: name, lastModifiedByEmail: email } =
      getEntityAttributes(attributes, [
        'lastModifiedByName',
        'lastModifiedByEmail',
      ]);
    return {
      name: name as string | undefined,
      email: email as string | undefined,
    };
  }

  return { name: 'User info missing' };
};

const getEmail = (
  attributes: EntityAttributes | null,
  field: Pick<APIFieldAttributes, 'name'>,
  val: string
) => {
  let label = val;
  if (field.name === 'createdByEmail') {
    const { createdByName } = getEntityAttributes(attributes, [
      'createdByName',
    ]);
    label = (createdByName as string) ?? '';
  } else if (field.name === 'lastModifiedByEmail') {
    const { lastModifiedByName } = getEntityAttributes(attributes, [
      'lastModifiedByName',
    ]);
    label = (lastModifiedByName as string) ?? '';
  }
  return (
    <a
      href={`mailto:${window.encodeURI(val)}`}
      target="_blank"
      rel="noopener noreferrer"
    >
      {label}
    </a>
  );
};

const getEntityAttributes = (
  attributes: EntityAttributes | null,
  keys: string[]
): Record<string, unknown> =>
  Object.fromEntries(
    keys.map(key => [key, attributes ? attributes[key] : undefined])
  );

const getUrl = (val: string) => (
  <a
    href={urlUtils.maybeRelativeUrl(val, isShiftXUrl(val))}
    target="_blank"
    rel="noopener noreferrer"
  >
    {val}
  </a>
);

const getDateRange = (
  [startDate, endDate]: DateRangeFieldValues,
  locale: Locale
) => {
  return startDate || endDate
    ? `${startDate ? formatDateShort(startDate, locale) : 'No start date'} - ${
        endDate ? formatDateShort(endDate, locale) : 'No end date'
      }`
    : '';
};

const FieldValueFormatWarningConatiner = styled.div`
  display: flex;
  align-items: center;
`;

const FieldValueFormatWarning = ({ value }: { value: unknown }) => {
  const popoverMessage = `Something is wrong. Field value "${value}" shouldn't have this format. This event has been logged, thank you.`;
  return (
    <FieldValueFormatWarningConatiner>
      {String(value)}
      <WithPopover content={popoverMessage}>
        <Icon
          iconName={IconName.WARNING}
          color={colors.yellow600}
          iconSize={IconSize.SMALL}
          style={{ marginLeft: 8 }}
        />
      </WithPopover>
    </FieldValueFormatWarningConatiner>
  );
};

type FormatFieldContentArgs = {
  value: string | number | string[] | DateRangeFieldValues | undefined | null;
  modelId: string;
  attributes: EntityAttributes | null;
  field: Pick<APIFieldAttributes, 'name' | 'type' | 'numberFormatOptions'>;
  isLocalDateFormat?: boolean;
  usersById: Record<ArdoqId, APIOrganizationUser>;
  attachmentsById: Record<ArdoqId, AttachmentWithContext>;
  i18nLocale: I18nLocale;
  locale: Locale;
};
export const formatFieldContent = ({
  value,
  modelId,
  attributes,
  field,
  isLocalDateFormat,
  usersById,
  attachmentsById,
  i18nLocale,
  locale,
}: FormatFieldContentArgs) => {
  if (
    (value === undefined || value === null) &&
    field.type !== APIFieldType.CHECKBOX
  ) {
    return '';
  }

  switch (field.type) {
    case APIFieldType.DATE_ONLY:
      return formatDateShort(value as string, locale);
    case APIFieldType.DATE_TIME:
      return (
        <FormattedDate
          date={value as string}
          isLocalDateFormat={isLocalDateFormat}
          i18nLocale={i18nLocale}
          locale={locale}
        />
      );
    case APIFieldType.NUMBER:
      return formatNumber(value as number, field.numberFormatOptions, locale);
    case APIFieldType.EMAIL:
      return getEmail(attributes, field, value as string);
    case APIFieldType.CHECKBOX:
      return typeof value === 'undefined' || value === null ? (
        <FlexBox>
          <Checkbox isChecked={null} />
          <FlexBox align="center">
            <NoValue />
          </FlexBox>
        </FlexBox>
      ) : (
        <Checkbox isChecked={Boolean(value)} />
      );
    case APIFieldType.URL:
      return getUrl(value as string);
    case APIFieldType.USER:
      return getUser(attributes, field, value as string, usersById);
    case APIFieldType.FILE:
      return getFileComponent(value as string, attachmentsById);
    case APIFieldType.TEXT_AREA:
      return <Markdown>{value as string}</Markdown>;
    case APIFieldType.DATE_ONLY_RANGE:
      return getDateRange(value as DateRangeFieldValues, locale);
    case APIFieldType.DATE_TIME_RANGE:
      return getDateRange(value as DateRangeFieldValues, locale);
    case APIFieldType.SELECT_MULTIPLE_LIST:
      // adding logging and error handling for https://ardoqcom.atlassian.net/browse/ARD-20653
      if (!Array.isArray(value)) {
        logError(
          new Error('Invalid value for select multiple list field'),
          'Invalid value for select multiple list field',
          { value, modelId, field }
        );
        return <FieldValueFormatWarning value={value} />;
      }
      return value.join(', ');

    default:
      // Adding this to make TypeScript happy.
      if (Array.isArray(value)) {
        return value.join(', ');
      }
      return value;
  }
};

export const getFieldValues = (
  attributes: EntityAttributes | null,
  fields: Array<
    | Pick<APIFieldAttributes, 'name' | '_id' | 'label' | 'type'>
    | APIFieldAttributes
  >,
  tagIds: ArdoqId[],
  referenceTypeName: string | null
) => {
  const simpleAttributes: string[] = [];

  const specialFieldValues = fields.reduce((acc: FieldValues, field) => {
    if (dateRangeOperations.isDateRangeField(field)) {
      // date range fields have a special format
      const { start, end } = field.dateTimeFields;
      const { [start.name]: startValue, [end.name]: endValue } =
        getEntityAttributes(attributes, [start.name, end.name]);
      acc[field.name] = startValue || endValue ? [startValue, endValue] : null;
    } else if (field.name === TAGS_FIELD_NAME) {
      // tags aren't attributes
      acc[field.name] = tagIds.length ? tagIds : null;
    } else {
      simpleAttributes.push(field.name);
    }

    return acc;
  }, {});

  const simpleFieldValues = getEntityAttributes(attributes, simpleAttributes);

  if (referenceTypeName !== null) {
    simpleFieldValues.type = referenceTypeName;
  }

  return { ...simpleFieldValues, ...specialFieldValues };
};
